// src/components/SimpleComponents/SimpleTime.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useEffect, useState } from 'react';
import { Input, InputNumber, Select, Space, Tooltip } from 'antd';
import translateX from 'utils/translateX';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const UNIT_MULTIPLIERS = {
  minutes: 1,
  hours: 60,
  days: 1440,
  weeks: 10080,
  months: 43200,
};

const ALL_UNIT_KEYS = ['minutes', 'hours', 'days', 'weeks', 'months'];

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleTime = ({
  value,
  onChange,

  // Optional prefix type selector — array of { value, label, tooltip? }
  // When provided, the stored value includes a `type` field
  types,
  defaultType,

  // Which units to display — subset of ALL_UNIT_KEYS, defaults to all
  units,

  // Default unit when no value is provided
  defaultUnit = 'minutes',

  // Number range
  min = 0,
  max = 999,

  // Other props
  disabled = false,
  style = {},
  placeholder,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const hasTypes = Array.isArray(types) && types.length > 0;
  const firstType = types?.[0]?.value;

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [numValue, setNumValue] = useState(value?.value ?? 0);
  const [selectedUnit, setSelectedUnit] = useState(value?.unit ?? defaultUnit);
  const [selectedType, setSelectedType] = useState(
    value?.type ?? defaultType ?? firstType ?? null,
  );

  // ANTD v6 fix: controlled open state ensures dropdown closes on first click
  const [openUnitSelect, setOpenUnitSelect] = useState(false);
  const [openTypeSelect, setOpenTypeSelect] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (value && typeof value === 'object') {
      setNumValue(value.value ?? 0);
      setSelectedUnit(value.unit ?? defaultUnit);
      if (hasTypes && value.type) setSelectedType(value.type);
    }
  }, [value, defaultUnit, hasTypes]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleNumberChange = useCallback(
    num => {
      const v = num ?? 0;
      const multiplier = UNIT_MULTIPLIERS[selectedUnit] ?? 1;
      setNumValue(v);
      const result = { value: v, unit: selectedUnit, minutes: v * multiplier };
      if (hasTypes) result.type = selectedType;
      onChange?.(result);
    },
    [selectedUnit, selectedType, hasTypes, onChange],
  );

  const handleUnitChange = useCallback(
    unit => {
      const multiplier = UNIT_MULTIPLIERS[unit] ?? 1;
      setSelectedUnit(unit);
      const result = { value: numValue, unit, minutes: numValue * multiplier };
      if (hasTypes) result.type = selectedType;
      onChange?.(result);
    },
    [numValue, selectedType, hasTypes, onChange],
  );

  const handleTypeChange = useCallback(
    type => {
      const multiplier = UNIT_MULTIPLIERS[selectedUnit] ?? 1;
      setSelectedType(type);
      const result = { value: numValue, unit: selectedUnit, minutes: numValue * multiplier };
      if (hasTypes) result.type = type;
      onChange?.(result);
    },
    [numValue, selectedUnit, hasTypes, onChange],
  );

  const handleUnitSelect = useCallback(() => setOpenUnitSelect(false), []);
  const handleUnitOpenChange = useCallback(v => setOpenUnitSelect(v), []);
  const handleTypeSelect = useCallback(() => setOpenTypeSelect(false), []);
  const handleTypeOpenChange = useCallback(v => setOpenTypeSelect(v), []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const allowedKeys = units || ALL_UNIT_KEYS;
  const unitOptions = allowedKeys
    .filter(k => UNIT_MULTIPLIERS[k] !== undefined)
    .map(k => ({ value: k, label: translateX(k) }));

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  const typeSelect = hasTypes ? (
    <Select
      open={openTypeSelect}
      onSelect={handleTypeSelect}
      onOpenChange={handleTypeOpenChange}
      value={selectedType}
      onChange={handleTypeChange}
      options={types}
      disabled={disabled}
      style={{ width: 120 }}
      optionRender={option =>
        option.data.tooltip ? (
          <Tooltip title={option.data.tooltip} placement="right">
            <span style={{ display: 'block', width: '100%' }}>{option.label}</span>
          </Tooltip>
        ) : (
          option.label
        )
      }
    />
  ) : null;

  const numberInput = (
    <InputNumber
      min={min}
      max={max}
      value={numValue}
      onChange={handleNumberChange}
      disabled={disabled}
      placeholder={placeholder ?? '0'}
      style={{ flex: 1 }}
    />
  );

  const unitSuffix =
    unitOptions.length === 1 ? (
      <Input
        readOnly
        value={unitOptions[0].label}
        style={{ width: 'auto', minWidth: 100, pointerEvents: 'none' }}
      />
    ) : (
      <Select
        open={openUnitSelect}
        onSelect={handleUnitSelect}
        onOpenChange={handleUnitOpenChange}
        value={selectedUnit}
        onChange={handleUnitChange}
        options={unitOptions}
        disabled={disabled}
        style={{ width: 130 }}
      />
    );

  return (
    <Space.Compact style={{ width: '100%', ...style }}>
      {typeSelect}
      {numberInput}
      {unitSuffix}
    </Space.Compact>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleTime;
